import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { ArrowUp, Square, Sparkles, Slash, X } from "lucide-react";

import CommentInput from "../../components/CommentInput";
import SlashPromptMenu from "./SlashPromptMenu";
import SuggestedPromptsPanel, {
  filterSelectableSuggestions,
} from "./SuggestedPromptsPanel";
import { isPromptUsable } from "../constants/chatConstants";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { rankPromptsForQuery } from "../utils/promptIntentUtils";
import {
  filterSlashPrompts,
  firstSelectableSlashIndex,
  isSlashPromptMode,
  slashPromptQuery,
} from "../utils/slashPromptUtils";
import type { ChatComposerProps, ManagingPromptCatalogEntry } from "../types/chat.types";
import "./chat-quill.css";

const ChatComposer: React.FC<
  ChatComposerProps & {
    onClearPrompt?: () => void;
    /** Replaces the default "Prompts ▾" button (e.g. popover trigger). */
    promptPickerSlot?: React.ReactNode;
  }
> = ({
  value,
  onChange,
  onSend,
  onStop,
  disabled = false,
  isLoading = false,
  isStreaming = false,
  placeholder,
  onOpenPromptPicker,
  onClearPrompt,
  activePromptName,
  promptPickerSlot,
  slashPrompts = [],
  recordType,
  onSelectPromptForComposer,
  hasCallRecordingWithTranscript,
  catalogExecuteReady = true,
  intentSuggestionsEnabled = false,
  intentSuggestionPrompts = [],
  showIntentSuggestionsWhenEmpty = false,
  onAskDirectly,
}) => {
  const { t } = useTranslation();
  const quillRef = useRef<any>(null);
  const onSendRef = useRef(onSend);
  const canSendRef = useRef(false);
  const slashOpenRef = useRef(false);
  const intentOpenRef = useRef(false);
  const [slashHighlight, setSlashHighlight] = useState(-1);
  const [intentHighlight, setIntentHighlight] = useState(-1);
  const [composerFocused, setComposerFocused] = useState(false);
  const debouncedQuery = useDebouncedValue(value, 120);

  onSendRef.current = onSend;

  const slashMode = isSlashPromptMode(value) && slashPrompts.length > 0;
  slashOpenRef.current = slashMode;

  const slashFiltered = useMemo(() => {
    if (!slashMode) return [];
    return filterSlashPrompts(slashPrompts, {
      recordType,
      query: slashPromptQuery(value),
      hasCallRecordingWithTranscript,
    });
  }, [slashMode, slashPrompts, recordType, value, hasCallRecordingWithTranscript]);

  useEffect(() => {
    if (!slashMode) {
      setSlashHighlight(-1);
      return;
    }
  }, [slashMode, value]);

  const showStop = isStreaming && !!onStop;
  const canSend =
    !disabled &&
    !isLoading &&
    !showStop &&
    catalogExecuteReady &&
    (value.trim().length > 0 || !!activePromptName);
  canSendRef.current = canSend;

  const resolvedPlaceholder =
    placeholder ??
    (activePromptName
      ? (t("Add focus (optional)…") as string)
      : intentSuggestionsEnabled
        ? (t("Ask anything… search CRM, deals, contacts") as string)
        : (t("Ask about this record… Type / for prompts") as string));

  const hasTypedQuery = value.trim().length > 0;
  const intentRankQuery = hasTypedQuery ? debouncedQuery : "";
  const intentSuggestions = useMemo(() => {
    if (!intentSuggestionsEnabled || intentSuggestionPrompts.length === 0) {
      return [];
    }
    return filterSelectableSuggestions(
      rankPromptsForQuery(intentSuggestionPrompts, intentRankQuery, {
        recordType,
        hasTranscript: hasCallRecordingWithTranscript,
      }),
      recordType,
      hasCallRecordingWithTranscript
    );
  }, [
    debouncedQuery,
    hasCallRecordingWithTranscript,
    intentRankQuery,
    intentSuggestionPrompts,
    intentSuggestionsEnabled,
    recordType,
    value,
  ]);

  const showIntentPanel =
    intentSuggestionsEnabled &&
    !slashMode &&
    !activePromptName &&
    (composerFocused || hasTypedQuery || showIntentSuggestionsWhenEmpty) &&
    (hasTypedQuery || intentSuggestions.length > 0 || showIntentSuggestionsWhenEmpty);

  const intentItemCount = !hasTypedQuery
    ? intentSuggestions.length
    : 1 + intentSuggestions.length;

  intentOpenRef.current = showIntentPanel && intentItemCount > 0;

  const handleEditorChange = useCallback(
    (content: { textContent?: string }) => {
      const raw = content.textContent ?? "";
      const text = raw === "\n" ? "" : raw.replace(/\n$/, "");
      onChange(text);
    },
    [onChange]
  );

  const selectSlashPrompt = useCallback(
    (prompt: ManagingPromptCatalogEntry) => {
      onSelectPromptForComposer?.(prompt);
      setSlashHighlight(-1);
    },
    [onSelectPromptForComposer]
  );

  const selectIntentPrompt = useCallback(
    (prompt: ManagingPromptCatalogEntry) => {
      onSelectPromptForComposer?.(prompt);
      setIntentHighlight(-1);
    },
    [onSelectPromptForComposer]
  );

  const handleIntentKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!intentOpenRef.current || intentItemCount === 0) return false;

      const chipMode = !hasTypedQuery;
      const maxIndex = intentItemCount - 1;

      if (e.key === "Escape") {
        e.preventDefault();
        setIntentHighlight(-1);
        return true;
      }

      const moveNext = () => {
        setIntentHighlight((prev) => {
          if (prev < 0) return 0;
          return Math.min(prev + 1, maxIndex);
        });
      };

      const movePrev = () => {
        setIntentHighlight((prev) => {
          if (prev < 0) return maxIndex;
          return Math.max(prev - 1, 0);
        });
      };

      if (chipMode ? e.key === "ArrowRight" || e.key === "ArrowDown" : e.key === "ArrowDown") {
        e.preventDefault();
        moveNext();
        return true;
      }

      if (chipMode ? e.key === "ArrowLeft" || e.key === "ArrowUp" : e.key === "ArrowUp") {
        e.preventDefault();
        movePrev();
        return true;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        const idx = intentHighlight >= 0 ? intentHighlight : 0;
        if (!chipMode && idx === 0) {
          onAskDirectly?.();
        } else {
          const promptIdx = chipMode ? idx : idx - 1;
          selectIntentPrompt(intentSuggestions[promptIdx]);
        }
        return true;
      }

      if (e.key === "Enter" && !e.shiftKey && intentHighlight >= 0) {
        e.preventDefault();
        if (!chipMode && intentHighlight === 0) {
          onAskDirectly?.();
        } else {
          const promptIdx = chipMode ? intentHighlight : intentHighlight - 1;
          selectIntentPrompt(intentSuggestions[promptIdx]);
        }
        return true;
      }

      if (e.key === "Enter" && !e.shiftKey && hasTypedQuery && intentHighlight < 0) {
        e.preventDefault();
        onAskDirectly?.();
        return true;
      }

      return false;
    },
    [
      hasTypedQuery,
      intentHighlight,
      intentItemCount,
      intentSuggestions,
      onAskDirectly,
      selectIntentPrompt,
    ]
  );

  const handleSlashKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!slashOpenRef.current || slashFiltered.length === 0) return false;

      if (e.key === "Escape") {
        e.preventDefault();
        onChange("");
        setSlashHighlight(-1);
        return true;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashHighlight((prev) => {
          const start = prev < 0 ? -1 : prev;
          for (let i = start + 1; i < slashFiltered.length; i++) {
            if (isPromptUsable(slashFiltered[i], recordType, { hasCallRecordingWithTranscript }).usable) {
              return i;
            }
          }
          return prev;
        });
        return true;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashHighlight((prev) => {
          const start = prev < 0 ? slashFiltered.length : prev;
          for (let i = start - 1; i >= 0; i--) {
            if (isPromptUsable(slashFiltered[i], recordType, { hasCallRecordingWithTranscript }).usable) {
              return i;
            }
          }
          return prev;
        });
        return true;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        const idx = firstSelectableSlashIndex(slashFiltered, recordType, {
          hasCallRecordingWithTranscript,
        });
        if (idx >= 0) {
          selectSlashPrompt(slashFiltered[idx]);
        }
        return true;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        if (slashHighlight >= 0 && slashFiltered[slashHighlight]) {
          const candidate = slashFiltered[slashHighlight];
          if (isPromptUsable(candidate, recordType, { hasCallRecordingWithTranscript }).usable) {
            e.preventDefault();
            selectSlashPrompt(candidate);
            return true;
          }
        }
      }

      return false;
    },
    [
      hasCallRecordingWithTranscript,
      onChange,
      recordType,
      selectSlashPrompt,
      slashFiltered,
      slashHighlight,
    ]
  );

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.enable(!disabled && !isLoading && !showStop);
  }, [disabled, isLoading, showStop]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const current = (quill.getText() || "").replace(/\n$/, "");
    if (current === value) return;
    if (value === "") {
      quill.setContents([]);
      return;
    }
    quill.setText(value);
  }, [value]);

  useEffect(() => {
    if (value !== "") return;
    const quill = quillRef.current;
    if (!quill) return;
    const current = (quill.getText() || "").replace(/\n$/, "").trim();
    if (current) {
      quill.setContents([]);
    }
  }, [value]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill?.root) return;

    const editorEl = quill.root as HTMLElement;
    const onKeyDown = (e: KeyboardEvent) => {
      if (handleSlashKeyDown(e)) return;
      if (handleIntentKeyDown(e)) return;
      if (e.key !== "Enter" || e.shiftKey || e.isComposing) return;
      if (document.querySelector(".ql-mention-list-container")) return;
      e.preventDefault();
      if (canSendRef.current) {
        onSendRef.current();
      }
    };

    editorEl.addEventListener("keydown", onKeyDown);
    return () => editorEl.removeEventListener("keydown", onKeyDown);
  }, [disabled, isLoading, isStreaming, value, activePromptName, handleSlashKeyDown, handleIntentKeyDown]);

  useEffect(() => {
    if (!showIntentPanel) {
      setIntentHighlight(-1);
    }
  }, [showIntentPanel, value]);

  return (
    <div className="chat-quill-composer border-t border-gray-200 bg-white px-3 pb-3 pt-2">
      {activePromptName ? (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
          <Sparkles className="h-3 w-3" />
          <span className="font-medium">{activePromptName}</span>
          {onClearPrompt ? (
            <button
              type="button"
              onClick={onClearPrompt}
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-600 hover:bg-blue-100"
              aria-label={t("Remove selected prompt")}
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        className="relative rounded-2xl border border-gray-200 bg-white px-3 py-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200"
        aria-label={t("Message") as string}
        onFocusCapture={() => setComposerFocused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setComposerFocused(false);
          }
        }}
      >
        {slashMode ? (
          <SlashPromptMenu
            prompts={slashPrompts}
            recordType={recordType}
            query={slashPromptQuery(value)}
            onSelect={selectSlashPrompt}
            hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
            highlightedIndex={slashHighlight}
            onHighlightChange={setSlashHighlight}
          />
        ) : showIntentPanel ? (
          <SuggestedPromptsPanel
            prompts={intentSuggestionPrompts}
            query={intentRankQuery}
            recordType={recordType}
            hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
            onSelect={selectIntentPrompt}
            onAskDirectly={onAskDirectly}
            askHighlighted={hasTypedQuery && intentHighlight === 0}
            promptHighlightedIndex={
              hasTypedQuery && intentHighlight > 0 ? intentHighlight - 1 : intentHighlight
            }
            highlightedIndex={intentHighlight}
            onHighlightChange={setIntentHighlight}
          />
        ) : null}

        <CommentInput
          placeholder={resolvedPlaceholder}
          editorContent={{ htmlContent: "", contents: {} }}
          members={fromJS([])}
          editorInputRef={quillRef}
          onChange={handleEditorChange}
          showToolbar={false}
          hideBorder
          minRows={2}
          lineHeightPx={22}
          editorClassName="chat-composer-editor"
          containerClassName="w-full"
        />

        <div className="relative z-10 mt-1 flex items-center justify-between">
          <div className="flex items-center gap-0.5">
            {onSelectPromptForComposer && slashPrompts.length > 0 ? (
              <button
                type="button"
                onClick={() => onChange("/")}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                aria-label={t("Type / for prompts") as string}
                title={t("Type / for prompts") as string}
              >
                <Slash className="h-3.5 w-3.5" />
              </button>
            ) : null}
            {promptPickerSlot ??
              (onOpenPromptPicker ? (
                <button
                  type="button"
                  onClick={onOpenPromptPicker}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                  aria-label={t("Choose a prompt")}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("Prompts")}
                </button>
              ) : (
                <span />
              ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden text-[11px] text-gray-400 sm:inline">
              {t("Shift+Enter for new line")}
            </span>
            {showStop ? (
              <button
                type="button"
                onClick={onStop}
                aria-label={t("Stop response")}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                aria-label={t("Send message")}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  canSend
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-200 text-gray-400"
                }`}
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatComposer;
